///
/// EarthBrowser
///
/// Created by Matt Giger on 4/2/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Earth } from "@/earthbrowser/activities/EarthActivity/Earth"
import { Crosshair } from "@/earthbrowser/activities/EarthActivity/utils/Crosshair"
import { UserLocationMarker } from "@/earthbrowser/activities/EarthActivity/utils/UserLocationMarker"
import { CameraDirector } from "@/earthbrowser/camera-director/CameraDirector"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { extractDivisionLabel, extractRoadShield } from "@/earthbrowser/entities/VectorTiles/MapLabelsOverlay"
import { VectorTileLayerNode } from "@/earthbrowser/entities/VectorTiles/VectorTileLayerNode"
import { engineBridge } from "@/earthbrowser/engineBridge"
import { geographic, wgs } from "@/earthbrowser/utils/geographic"
import { registerCrosshairStatsProvider, registerUserLocationStatsProvider } from "@/earthbrowser/utils/tileStatsRegistry"
import { applyViewportInsets } from "@/earthbrowser/utils/viewportInsets"
import { Perspective } from "@stovokor/camera/Perspective"
import { ActivityBase } from "@stovokor/engine/ActivityBase"
import { RenderState } from "@stovokor/engine/RenderState"
import { rgba } from "@stovokor/math/color"
import { ray, sphere } from "@stovokor/math"
import { trig } from "@stovokor/math/math"
import { time } from "@stovokor/math/time"
import { dot, vec3 } from "@stovokor/math/vector"
import { Sky } from "./sky/Sky"

const cCameraMovementThreshold = 1e-6
// Per-frame bearing change (radians) above which the crosshair shows its
// rotation ring with a north tick
const cBearingRotationThreshold = 1e-4

// Political boundaries served from borders.pmtiles via the tile-server worker
// (built by tools/vector-tiles/build-borders-pmtiles.sh from Overture divisions).
// Set VITE_TILE_SERVER_URL (e.g. http://localhost:5174) to use a local tile-server.
const cTileServerUrl = import.meta.env.VITE_TILE_SERVER_URL || "https://tiles.earthbrowser.com"
// Bump when borders.pmtiles contents change: busts the browser HTTP cache and
// the worker edge cache (via the URL) and the client SQLite cache (via source)
const cBordersVersion = 4 // v4: added division_labels point layer
const cBordersUrlTemplate = `${cTileServerUrl}/borders/{z}/{x}/{y}.pbf?v=${cBordersVersion}`

// Roads served from roads.pmtiles (built by tools/vector-tiles/build-roads-pmtiles.sh
// from Overture transportation segments): lines by class + road_shields points
const cRoadsVersion = 4 // v4: official shield artwork symbols on road_shields
const cRoadsUrlTemplate = `${cTileServerUrl}/roads/{z}/{x}/{y}.pbf?v=${cRoadsVersion}`

export class EarthActivity extends ActivityBase {
	public earth: Earth
	public sky: Sky
	public borders: VectorTileLayerNode
	public roads: VectorTileLayerNode
	public cameraDirector: CameraDirector
	private crosshair: Crosshair
	private userLocationMarker: UserLocationMarker
	private unsubscribeBorders?: () => void
	private lastCameraPosition = vec3.zero
	private lastCameraForward = vec3.zero
	private lastCameraBearing = 0

	constructor(context: EarthBrowserContext) {
		super(context)

		this.earth = new Earth(context)
		this.sky = new Sky(context)
		this.crosshair = new Crosshair(context)
		registerCrosshairStatsProvider(() => ({
			...this.crosshair.stats,
			motionSource: this.earth.cameraAdapter.earthCameraState.motionSource
		}))
		this.userLocationMarker = new UserLocationMarker(context)
		registerUserLocationStatsProvider(() => ({
			marker: this.userLocationMarker.stats,
			following: this.cameraDirector.following,
			state: engineBridge.getState()?.dialog.userLocation,
		}))

		this.borders = new VectorTileLayerNode(context, {
			source: `borders-v${cBordersVersion}`,
			urlTemplate: cBordersUrlTemplate,
			// A local tile-server (VITE_TILE_SERVER_URL) allows anonymous access
			// in dev, so skip tile-token requests that 401 while signed out
			authenticated: !import.meta.env.VITE_TILE_SERVER_URL,
			minLevel: 0,
			maxLevel: 10,
			labels: {
				sourceLayer: "division_labels",
				extract: extractDivisionLabel,
				// Region names fade with the region boundaries' density
				densityFadeByKind: { region: "region" }
			},
			// Hierarchy by weight and temperature: countries a warm bone white,
			// regions thinner, cooler and fainter so the two read as levels
			// rather than one tangle of white lines
			styles: [
				{
					id: "country",
					sourceLayer: "boundaries",
					color: rgba(1.0, 0.96, 0.88, 0.78),
					width: 1.5,
					casingWidth: 1.5,
					casingColor: rgba(0, 0, 0, 0.3),
					filter: props => props.subtype === "country" && props.class !== "maritime"
				},
				{
					id: "region",
					sourceLayer: "boundaries",
					color: rgba(0.80, 0.86, 0.94, 0.65),
					width: 1.25,
					casingWidth: 1.25,
					casingColor: rgba(0, 0, 0, 0.35),
					// Long dash, short gap: the cartographic mark for a sub-national line
					dash: [8, 4],
					// Countries whose first-level divisions are tiny (Slovenian
					// občine, Croatian županije) would draw as a scribble from
					// orbit: fade where lines would sit closer than this on screen
					densityFade: [6, 18],
					// Bloom in over ~2000 km of descent instead of popping at one level
					minLevel: 3,
					fadeAltitudeKm: [4200, 2200],
					filter: props => props.subtype === "region" && props.class !== "maritime"
				}
			]
		})
		this.earth.addNode(this.borders)

		this.roads = new VectorTileLayerNode(context, {
			source: `roads-v${cRoadsVersion}`,
			urlTemplate: cRoadsUrlTemplate,
			authenticated: !import.meta.env.VITE_TILE_SERVER_URL,
			minLevel: 5,
			maxLevel: 12,
			labels: { sourceLayer: "road_shields", extract: extractRoadShield, maxLabels: 28 },
			styles: [
				{
					id: "motorway",
					sourceLayer: "roads",
					color: rgba(1.0, 0.72, 0.25, 0.9),
					width: 3.0,
					filter: props => props.class === "motorway"
				},
				{
					id: "trunk",
					sourceLayer: "roads",
					color: rgba(1.0, 0.85, 0.45, 0.8),
					width: 2.5,
					minLevel: 6,
					filter: props => props.class === "trunk"
				},
				{
					id: "primary",
					sourceLayer: "roads",
					color: rgba(1, 1, 1, 0.7),
					width: 2.0,
					minLevel: 8,
					filter: props => props.class === "primary"
				},
				{
					id: "secondary",
					sourceLayer: "roads",
					color: rgba(1, 1, 1, 0.45),
					width: 1.5,
					minLevel: 10,
					filter: props => props.class === "secondary"
				}
			]
		})
		this.earth.addNode(this.roads)

		// Mirror the Layers panel toggles (earthbrowser.visibleDatasets) onto
		// the borders node, same pattern as the DatasetLayer subclasses.
		// "division_labels" is a sub-layer of "borders" (labels only show
		// while borders are on), defaulting to off. Without a store (embed
		// builds) the nodes keep their defaults; the embed toggles .visible
		// directly from its URL params.
		const applyBordersVisibility = () => {
			const datasets = engineBridge.getState()?.earthbrowser.visibleDatasets
			if (!datasets) return
			const visible = datasets.includes("borders")
			const labelsVisible = datasets.includes("division_labels")
			const roadsVisible = datasets.includes("roads")
			if (visible !== this.borders.visible) {
				this.borders.visible = visible
			}
			if (labelsVisible !== this.borders.labelsVisible) {
				this.borders.labelsVisible = labelsVisible
			}
			if (roadsVisible !== this.roads.visible) {
				this.roads.visible = roadsVisible
			}
		}
		applyBordersVisibility()
		this.unsubscribeBorders = engineBridge.subscribe(applyBordersVisibility)

		this.worldNodes = [this.earth, this.sky]
		this.uiNodes = [this.userLocationMarker, this.crosshair]

		// Automated camera motion (view fly-to now; orbit/tours in later phases)
		this.cameraDirector = new CameraDirector(context, this.earth.cameraAdapter.earthCameraState)

		this.context.focusManager.setDefaultFocus(this.earth)
	}

	public get renderReady(): boolean {
		return this.earth.renderReady && this.sky.renderReady
	}

	public override destroy(): void {
		this.unsubscribeBorders?.()
		this.cameraDirector.destroy()
		super.destroy()
	}

	public start() {
		this.addTimer(this.earth.cacheTask)

		const clock = (this.context as EarthBrowserContext).clock
		this.addFrameTask(() => {
			const now = new time()
			clock.beginFrame(now)
		})

		this.addFrameTask(() => this.updateSky())
		this.addFrameTask(() => this.updateViewportInsets())
		this.addFrameTask(() => this.updateCrosshair())
		this.addFrameTask(() => this.cameraDirector.update())
		// After the director so the marker projects through the pose it
		// just wrote (in follow mode the dot then stays exactly centered)
		this.addFrameTask(() => this.updateUserLocationMarker())

		// Give Earth focus to initialize the camera
		this.earth.requestFocus()
	}

	/**
	 * Shift the world camera's projection so the view axis sits in the
	 * middle of the globe area the React chrome leaves uncovered (the data
	 * dock on the right). Runs before the viewport rebuilds its matrices
	 * for the frame, so picking and the focused-entity callout agree.
	 */
	private updateViewportInsets(): void {
		const projection = this.context.focusManager.getViewportHandler()?.camera.projection
		if (projection instanceof Perspective) {
			applyViewportInsets(projection)
		}
	}

	private updateCrosshair(): void {
		const focusManager = this.context.focusManager
		const focusedNode = focusManager.getFocusedNode()
		const hasFocusElement = focusedNode !== undefined && focusedNode.id !== this.earth.id

		if (hasFocusElement) {
			this.crosshair.dismiss()
			return
		}

		const camera = focusManager.getViewportHandler()?.camera
		if (!camera) return

		const pos = camera.position
		const fwd = camera.state.forward
		const posDiff = pos.subtract(this.lastCameraPosition)
		const fwdDiff = fwd.subtract(this.lastCameraForward)
		const isMoving = dot(posDiff, posDiff) > cCameraMovementThreshold
			|| dot(fwdDiff, fwdDiff) > cCameraMovementThreshold

		this.lastCameraPosition = pos
		this.lastCameraForward = fwd

		const camState = this.earth.cameraAdapter.earthCameraState
		const bearingDelta = Math.atan2(
			Math.sin(camState.bearing - this.lastCameraBearing),
			Math.cos(camState.bearing - this.lastCameraBearing)
		)
		const isRotating = Math.abs(bearingDelta) > cBearingRotationThreshold
		this.lastCameraBearing = camState.bearing

		// Only gestures show the crosshair: it marks where the user's aim is
		// taking the camera. Automated motion (fly-to, zoom buttons, crane,
		// orbit, tours) has a known destination or no aim at all, so the
		// reticle would just sit over the globe for the whole move. Motion
		// tracking above still runs so the frame after an automated move
		// stops doesn't flash the crosshair.
		const userMoving = isMoving && camState.motionSource === "user"

		if (userMoving) {
			// The crosshair marks the fulcrum (where the camera's forward ray
			// meets the Earth), not the point under the camera - they differ
			// whenever the camera is tilted
			const fulcrum = new sphere(wgs.a + 2).closestPoint(new ray(camera.position, camera.state.forward))
			const geo = new geographic(fulcrum)
			const ebcontext = this.context as EarthBrowserContext
			const elevationM = ebcontext.baseElevation.loaded
				? ebcontext.baseElevation.getElevation(geo)
				: undefined
			this.crosshair.setPose({
				lat: geo.latitude * trig.deg,
				lon: geo.longitude * trig.deg,
				elevationM,
				altitudeKm: camState.spherical.radius - wgs.a,
				bearing: camState.bearing,
				rotating: isRotating
			})
			this.crosshair.show(true)
		} else if (isMoving) {
			// An automated move, possibly cutting a gesture short: no reticle,
			// and no settle readout of the pose it left behind
			this.crosshair.dismiss()
		} else {
			// Settled: the reticle fades out and the readout appears
			this.crosshair.show(false)
		}
	}

	/** Keep the "you are here" marker on the projected device location */
	private updateUserLocationMarker(): void {
		const camera = this.context.focusManager.getViewportHandler()?.camera
		if (!camera) return
		this.userLocationMarker.update(camera)
	}

	/**
	 * Advance the sky ephemeris to the scene clock and publish the sun
	 * direction every renderer lights the scene with
	 */
	private updateSky(): void {
		const ebcontext = this.context as EarthBrowserContext
		this.sky.update(ebcontext.clock.clockTime)

		// hacky global state
		RenderState.lightDirection = this.sky.ephemeris.sunDirection
	}
}
